/* eslint-disable */
/**
 * POSRooms - Peace of Sleep Room Management
 * Migrated to use DataTable component with global CSS styling.
 * Supports List and Board views.
 */

import { useState, useEffect, useMemo } from 'react';
import { formatDate } from '../../../utils/formatters';
import { useAuth } from '../../../contexts/AuthContext';
import { usePOS } from '../../../contexts/POSContext';
import { usePagePermission } from '../../../hooks/usePagePermission';
import AccessDenied from '../../../components/AccessDenied';
import LoadingSpinner from '../../../components/LoadingSpinner';
import ErrorDisplay from '../../../components/ErrorDisplay';
import DataTable, { StatusBadge, type Column } from '../../../components/DataTable';
import SearchSelect from '../../../components/SearchSelect';


// Permission constants
const PERMISSIONS = {
  CREATE: 'create',
  EDIT: 'edit',
  DELETE: 'delete',
  BULK_IMPORT: 'bulk_import'
};

import {
  ROOM_STATUS_CONFIG as STATUS_CONFIG_TYPED,
  ROOM_TYPES,
  calculateRoomStatus as calcStatus,
} from '../../../config/posRoomStatus';

// Alias for backward compat with 'pending' key used throughout this file
const STATUS_CONFIG = { ...STATUS_CONFIG_TYPED, pending: STATUS_CONFIG_TYPED['not-serviced'] };

function calculateRoomStatus(room: Record<string, any>, propertyTier: string | undefined) {
  const status = calcStatus(room.last_service_date, propertyTier ?? '');
  return status === 'not-serviced' ? 'pending' : status;
}

function POSRooms() {
  const { supabase, hasPermission } = useAuth();
  const {
    selectedPropertyId,
    setSelectedPropertyId,
    userProperties,
    propertiesLoading: _propertiesLoading,
    hasMultipleProperties: _hasMultipleProperties,
    isCMBStaff,
    canEditRooms
  } = usePOS();
  const { canAccess, isLoading: permissionLoading } = usePagePermission('pos_rooms');

  // Permission checks
  const canCreate = isCMBStaff ? hasPermission('pos_rooms', PERMISSIONS.CREATE) : canEditRooms;
  const canEdit = isCMBStaff ? hasPermission('pos_rooms', PERMISSIONS.EDIT) : canEditRooms;
  const canDelete = isCMBStaff ? hasPermission('pos_rooms', PERMISSIONS.DELETE) : false;
  const canBulkImport = isCMBStaff ? hasPermission('pos_rooms', PERMISSIONS.BULK_IMPORT) : canEditRooms;

  // State
  const [rooms, setRooms] = useState<Record<string, any>[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [viewingRoom, setViewingRoom] = useState<Record<string, any> | null>(null);
  const [editingRoom, setEditingRoom] = useState<Record<string, any> | null>(null);
  const [showImportModal, setShowImportModal] = useState(false);

  // View mode (list or card)
  const [viewMode, setViewMode] = useState(() => {
    const saved = localStorage.getItem('posRoomsViewMode');
    // Migrate 'board' to 'card' for consistency
    return saved === 'board' ? 'card' : (saved || 'list');
  });

  // Save preferences
  useEffect(() => {
    localStorage.setItem('posRoomsViewMode', viewMode);
  }, [viewMode]);

  // Load rooms when property changes
  useEffect(() => {
    if (selectedPropertyId) {
      loadRooms();
    } else {
      setRooms([]);
      setLoading(false);
    }
  }, [selectedPropertyId]);

  const loadRooms = async () => {
    if (!selectedPropertyId) {
      return;
    }

    try {
      setLoading(true);
      setError(null);


      const { data, error: fetchError } = await supabase
        .from('hosp_rooms')
        .select('*')
        .eq('property_id', selectedPropertyId)
        .limit(10000);


      if (fetchError) throw fetchError;

      const property = userProperties.find(p => p.id === selectedPropertyId);
      const enriched = (data || []).map(room => ({
        ...room,
        status: calculateRoomStatus(room, property?.hospitality_tier),
        roomType: ROOM_TYPES.find(t => t.value === room.room_type)?.label || room.room_type || '-',
        lastServiceDate: room.last_service_date ? formatDate(room.last_service_date) : '-'
      }));

      // Sort rooms numerically (1, 2, 3... 10, 11) not alphabetically (1, 10, 11, 2)
      enriched.sort((a, b) =>
        (a.room_number || '').localeCompare(b.room_number || '', undefined, { numeric: true })
      );

      setRooms(enriched);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const selectedProperty = userProperties.find(p => p.id === selectedPropertyId);

  // Filter options
  const statusOptions = useMemo(() => [
    { value: 'certified', label: 'Certified' },
    { value: 'due-soon', label: 'Due Soon' },
    { value: 'overdue', label: 'Overdue' },
    { value: 'pending', label: 'Not Serviced' }
  ], []);

  const roomTypeOptions = useMemo(() => ROOM_TYPES, []);

  // Group by status for board view
  const roomsByStatus = useMemo(() => {
    const grouped: Record<string, Record<string, any>[]> = {
      certified: [],
      'due-soon': [],
      overdue: [],
      pending: []
    };

    rooms.forEach(room => {
      const bucket = grouped[room.status];
      if (bucket) {
        bucket.push(room);
      }
    });
    return grouped;
  }, [rooms]);

  // DataTable columns
  const columns: Column[] = [
    {
      key: 'room_number',
      label: 'Room',
      sortable: true,
      render: (row) => <span className="table-cell-name">{row.room_number}</span>
    },
    {
      key: 'floor',
      label: 'Floor',
      align: 'center',
      sortable: true,
      render: (row) => row.floor || '-'
    },
    {
      key: 'bed_count',
      label: 'Beds',
      align: 'center',
      sortable: true,
      render: (row) => row.bed_count || 1
    },
    {
      key: 'roomType',
      label: 'Bed Type',
      sortable: true
    },
    {
      key: 'lastServiceDate',
      label: 'Last Service',
      sortable: true
    },
    {
      key: 'status',
      label: 'Status',
      align: 'center',
      sortable: true,
      render: (row) => {
        const config = (STATUS_CONFIG as Record<string, typeof STATUS_CONFIG.pending>)[row.status] || STATUS_CONFIG.pending;
        return <StatusBadge bg={config.bg} color={config.color}>{config.label}</StatusBadge>;
      }
    }
  ];

  // Permission check
  if (permissionLoading) return <LoadingSpinner />;
  if (!canAccess) return <AccessDenied feature="pos_rooms" />;


  return (
    <div>
      {/* Property Selector - persists across views */}
      <div className="property-selector">
        <label className="property-selector-label">
          Property:
        </label>
        <SearchSelect
          width="inline"
          options={[
            { value: '', label: '— Select a property —' },
            ...userProperties.map(p => ({ value: p.id, label: p.property_name })),
          ]}
          value={selectedPropertyId}
          onChange={setSelectedPropertyId}
        />
        {selectedProperty && (
          <span className="pos-status-indicator">
            {selectedProperty.hospitality_tier || 'standard'} · {rooms.length} room{rooms.length !== 1 ? 's' : ''}
          </span>
        )}
      </div>

      {/* View Toggle Tabs */}
      <div className="page-tabs">
        <button
          className={`page-tab ${viewMode === 'list' ? 'active' : ''}`}
          onClick={() => setViewMode('list')}
        >
          📋 List View
        </button>
        <button
          className={`page-tab ${viewMode === 'card' ? 'active' : ''}`}
          onClick={() => setViewMode('card')}
        >
          🗂️ Card View
        </button>
      </div>

      {/* List View */}
      {viewMode === 'list' && (
        <DataTable
          columns={columns}
          data={rooms}
          loading={loading}
          searchable={true}
          searchPlaceholder="Search room number, floor..."
          searchKeys={['room_number', 'floor', 'roomType']}
          filters={[
            { key: 'status', label: 'Status', options: statusOptions },
            { key: 'room_type', label: 'Type', options: roomTypeOptions }
          ]}
          defaultSort={{ key: 'room_number', direction: 'asc' }}
          headerActions={{
            primary: canCreate && selectedPropertyId ? {
              label: '+ Room',
              onClick: () => setEditingRoom({ isNew: true, property_id: selectedPropertyId })
            } : undefined,
            onSync: loadRooms,
            overflow: canBulkImport && selectedPropertyId ? [
              { label: 'Import CSV', icon: '📥', onClick: () => setShowImportModal(true) }
            ] : []
          }}
          actions={(row) => (
                <>
                  <span
                    className="action-icon"
                    onClick={() => setViewingRoom(row)}
                    title="View"
                  >
                    👁️
                  </span>
                  {canEdit && (
                    <span
                      className="action-icon"
                      onClick={() => setEditingRoom(row)}
                      title="Edit"
                    >
                      ✏️
                    </span>
                  )}
                </>
              )}
              pageSize={25}
              emptyMessage={selectedPropertyId ? "No rooms found. Add your first room to get started." : "Select a property to view rooms"}
            />
          )}

          {/* Card View */}
          {viewMode === 'card' && (
            <div className="card">
              {canCreate && selectedPropertyId && (
                <div className="list-toolbar justify-end">
                  <button
                    className="btn btn-primary"
                    onClick={() => setEditingRoom({ isNew: true, property_id: selectedPropertyId })}
                  >
                    + Add Room
                  </button>
                </div>
              )}
              {loading && <LoadingSpinner />}
              {error && <ErrorDisplay error={error} onRetry={() => { loadRooms(); }} />}
              {!loading && !error && !selectedPropertyId && (
                <div className="empty-state">
                  <p className="empty-state-message">Select a property above to view rooms</p>
                </div>
              )}
              {!loading && !error && selectedPropertyId && (
                <div className="status-grid">
                  {Object.entries(STATUS_CONFIG).map(([statusId, config]) => (
                    <div key={statusId} className="status-column">
                      <div
                        className="status-column-header pos-service-card"
                        style={{ borderTopColor: config.color }}
                      >
                        <span className="status-column-title">{config.label}</span>
                        <span
                          className="status-column-count"
                          style={{ background: config.bg, color: config.color }}
                        >
                          {roomsByStatus[statusId]?.length || 0}
                        </span>
                      </div>
                      <div className="status-column-body">
                        {roomsByStatus[statusId]?.length === 0 ? (
                          <div className="empty-state text-sm">
                            No rooms
                          </div>
                        ) : (
                          roomsByStatus[statusId]?.slice(0, 15).map(room => (
                            <div
                              key={room.id}
                              className="status-card"
                              onClick={() => setViewingRoom(room)}
                            >
                              <div className="status-card-title">
                                Room {room.room_number}
                              </div>
                              <div className="status-card-meta">
                                Floor {room.floor || '?'} | {room.bed_count || 1} bed(s)
                              </div>
                              <div className="status-card-meta">
                                Last: {room.lastServiceDate}
                              </div>
                            </div>
                          ))
                        )}
                        {(roomsByStatus[statusId]?.length ?? 0) > 15 && (
                          <div className="text-center text-muted text-xs py-sm">
                            +{(roomsByStatus[statusId]?.length ?? 0) - 15} more
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

      {/* View Modal */}
      {viewingRoom && (
        <RoomViewModal
          room={viewingRoom}
          property={selectedProperty}
          onClose={() => setViewingRoom(null)}
          onEdit={() => {
            setViewingRoom(null);
            setEditingRoom(viewingRoom);
          }}
          canEdit={canEdit}
        />
      )}

      {/* Edit Modal */}
      {editingRoom && (
        <RoomEditModal
          room={editingRoom}
          onClose={() => setEditingRoom(null)}
          onSave={async () => {
            setEditingRoom(null);
            await loadRooms();
          }}
          onDelete={async () => {
            setEditingRoom(null);
            await loadRooms();
          }}
          supabase={supabase}
          canDelete={canDelete}
        />
      )}

      {/* CSV Import Modal */}
      {showImportModal && (
        <CSVImportModal
          propertyId={selectedPropertyId}
          onClose={() => setShowImportModal(false)}
          onImport={loadRooms}
          supabase={supabase}
        />
      )}
    </div>
  );
}

// View Modal
function RoomViewModal({ room, property: _property, onClose, onEdit, canEdit }: {
  room: Record<string, any>;
  property?: Record<string, any>;
  onClose: () => void;
  onEdit: () => void;
  canEdit: boolean;
}) {
  const statusConfig = (STATUS_CONFIG as Record<string, typeof STATUS_CONFIG.pending>)[room.status] || STATUS_CONFIG.pending;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2 className="modal-title">Room {room.room_number}</h2>
          <button className="modal-close" onClick={onClose}>&times;</button>
        </div>
        <div className="modal-body">
          <div className="view-section">
            <div className="section-title">Room Details</div>
            <div className="view-grid">
              <div className="view-row">
                <span className="view-label">Floor</span>
                <span>{room.floor || '-'}</span>
              </div>
              <div className="view-row">
                <span className="view-label">Beds</span>
                <span>{room.bed_count || 1}</span>
              </div>
              <div className="view-row">
                <span className="view-label">Bed Type</span>
                <span>{room.roomType}</span>
              </div>
            </div>
          </div>

          <div className="view-section">
            <div className="section-title">Service Status</div>
            <div className="view-grid">
              <div className="view-row">
                <span className="view-label">Status</span>
                <StatusBadge bg={statusConfig.bg} color={statusConfig.color}>{statusConfig.label}</StatusBadge>
              </div>
              <div className="view-row">
                <span className="view-label">Last Service</span>
                <span>{room.lastServiceDate}</span>
              </div>
            </div>
          </div>

          {room.notes && (
            <div className="view-section border-none">
              <div className="section-title">Notes</div>
              <div>{room.notes}</div>
            </div>
          )}
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Close</button>
          {canEdit && (
            <button className="btn btn-primary" onClick={onEdit}>
              Edit Room
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// Edit Modal
function RoomEditModal({ room, onClose, onSave, onDelete, supabase, canDelete }: {
  room: Record<string, any>;
  onClose: () => void;
  onSave: () => void;
  onDelete: () => void;
  supabase: any;
  canDelete: boolean;
}) {
  const isNew = room.isNew;
  const [activeTab, setActiveTab] = useState('details');
  const [form, setForm] = useState({
    room_number: room.room_number || '',
    floor: room.floor || '',
    bed_count: room.bed_count || 1,
    room_type: room.room_type || 'double',
    notes: room.notes || '',
    property_id: room.property_id
  });
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!form.room_number.trim()) {
      alert('Room number is required');
      return;
    }

    if (!form.property_id) {
      alert('No property selected');
      return;
    }

    setSaving(true);
    try {
      const data = {
        ...form,
        floor: form.floor || null,  // Keep as text (DB column is text, not integer)
        bed_count: parseInt(form.bed_count) || 1
      };

      if (isNew) {
        const { data: inserted, error } = await supabase
          .from('hosp_rooms')
          .insert([data])
          .select();
        if (error) throw error;
        // Check if RLS silently blocked the insert
        if (!inserted || inserted.length === 0) {
          throw new Error('Unable to save room. You may not have permission to add rooms to this property.');
        }
      } else {
        const { data: updated, error } = await supabase
          .from('hosp_rooms')
          .update(data)
          .eq('id', room.id)
          .select();
        if (error) throw error;
        // Check if RLS silently blocked the update
        if (!updated || updated.length === 0) {
          throw new Error('Unable to save room. You may not have permission to edit rooms for this property.');
        }
      }
      // Success - close modal and refresh list
      onSave();
    } catch (err) {
      alert('Error saving room: ' + (err as any).message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!confirm(`Delete room "${room.room_number}"?\n\nThis will also delete all service history. This cannot be undone.`)) return;

    try {
      const { error } = await supabase.from('hosp_rooms').delete().eq('id', room.id);
      if (error) throw error;
      await onDelete();
    } catch (err) {
      alert('Error deleting room: ' + (err as any).message);
    }
  };

  const tabs = [
    { id: 'details', label: 'Details' },
    ...(!isNew && canDelete ? [{ id: 'danger', label: 'Danger Zone' }] : [])
  ];

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2 className="modal-title">{isNew ? 'Add Room' : 'Edit Room'}</h2>
          <button className="modal-close" onClick={onClose}>&times;</button>
        </div>

        {/* Tabs */}
        {tabs.length > 1 && (
          <div className="modal-tabs">
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`modal-tab ${activeTab === tab.id ? 'active' : ''} ${tab.id === 'danger' ? 'danger' : ''}`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        )}

        <div className="modal-body">
          {/* Details Tab */}
          {activeTab === 'details' && (
            <>
              <div className="form-group">
                <label className="form-label">Room Number *</label>
                <input
                  type="text"
                  className="form-input"
                  value={form.room_number}
                  onChange={(e) => setForm({ ...form, room_number: e.target.value })}
                  placeholder="e.g., 101, Suite A"
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Floor</label>
                  <input
                    type="number"
                    className="form-input"
                    value={form.floor}
                    onChange={(e) => setForm({ ...form, floor: e.target.value })}
                    placeholder="e.g., 1"
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Beds</label>
                  <input
                    type="number"
                    className="form-input"
                    value={form.bed_count}
                    onChange={(e) => setForm({ ...form, bed_count: e.target.value })}
                    min="1"
                  />
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">Bed Type</label>
                <select
                  className="form-input"
                  value={form.room_type}
                  onChange={(e) => setForm({ ...form, room_type: e.target.value })}
                >
                  {ROOM_TYPES.map(t => (
                    <option key={t.value} value={t.value}>{t.label}</option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label className="form-label">Notes</label>
                <textarea
                  className="form-input"
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  rows={2}
                  placeholder="Optional notes..."
                />
              </div>
            </>
          )}

          {/* Danger Zone Tab */}
          {activeTab === 'danger' && (
            <div className="danger-zone">
              <div className="font-bold text-error mb-sm">
                Delete Room
              </div>
              <p className="text-sm text-error mb-md">
                This will permanently delete room "{room.room_number}" and all associated service history.
                This action cannot be undone.
              </p>
              <button className="btn btn-danger" onClick={handleDelete}>
                Delete Room
              </button>
            </div>
          )}
        </div>

        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          {activeTab === 'details' && (
            <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
              {saving ? 'Saving...' : (isNew ? 'Add Room' : 'Save Changes')}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// CSV Import Modal
function CSVImportModal({ propertyId, onClose, onImport, supabase }: {
  propertyId: string;
  onClose: () => void;
  onImport: () => void;
  supabase: any;
}) {
  const [importData, setImportData] = useState<Record<string, any>[]>([]);
  const [importing, setImporting] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [fileName, setFileName] = useState('');

  const downloadTemplate = () => {
    const template = 'room_number,floor,bed_type,bed_count,notes\n101,1,king,1,Sample room\n102,1,queen,2,\n103,2,double,1,';
    const blob = new Blob([template], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'rooms_import_template.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const parseCSV = (text: string) => {
    const lines = text.trim().split('\n');
    if (lines.length < 2) {
      alert('CSV file is empty or has no data rows');
      return;
    }

    const headers = lines[0]!.split(',').map(h => h.trim().toLowerCase().replace(/[^a-z_]/g, '_'));
    const data: Record<string, string>[] = [];

    for (let i = 1; i < lines.length; i++) {
      const line = lines[i]!;
      const values: string[] = line.match(/(".*?"|[^",\s]+)(?=\s*,|\s*$)/g) || line.split(',');
      const room: Record<string, string> = {};
      headers.forEach((header, idx) => {
        let val = (values[idx] ?? '').trim() || '';
        if (val.startsWith('"') && val.endsWith('"')) {
          val = val.slice(1, -1);
        }
        room[header] = val;
      });
      if (room.room_number) {
        data.push(room);
      }
    }

    setImportData(data);
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setFileName(file.name);
    const reader = new FileReader();
    reader.onload = (event) => parseCSV(event.target?.result as string);
    reader.readAsText(file);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file && file.name.endsWith('.csv')) {
      setFileName(file.name);
      const reader = new FileReader();
      reader.onload = (event) => parseCSV(event.target?.result as string);
      reader.readAsText(file);
    }
  };

  const handleImport = async () => {
    if (importData.length === 0 || !propertyId) return;

    setImporting(true);
    try {
      const rooms = importData.map(r => ({
        property_id: propertyId,
        room_number: r.room_number,
        floor: r.floor || null,  // Keep as text (DB column is text, not integer)
        room_type: r.bed_type || r.room_type || 'double',
        bed_count: parseInt(r.bed_count) || 1,
        notes: r.notes || null,
        status: 'active'
      }));

      // Check for duplicate room_numbers within this property
      const roomNumbers = rooms.map(r => r.room_number);
      const { data: existing } = await supabase
        .from('hosp_rooms')
        .select('room_number')
        .eq('property_id', propertyId)
        .in('room_number', roomNumbers);

      const existingNumbers = new Set((existing || []).map((r: Record<string, any>) => r.room_number));
      const newRooms = rooms.filter(r => !existingNumbers.has(r.room_number));
      const skipped = rooms.length - newRooms.length;

      if (newRooms.length === 0) {
        alert(`All ${rooms.length} rooms already exist in this property.`);
        return;
      }

      const { error } = await supabase.from('hosp_rooms').insert(newRooms);
      if (error) throw error;

      // Belt + braces nudge: fire the AFTER trigger on hosp_rooms
      // for every active room on this property so total_rooms /
      // total_beds on hosp_properties always lands. Single source of
      // truth for the totals is the trigger; the application no
      // longer derives them itself.
      await supabase
        .from('hosp_rooms')
        .update({ updated_at: new Date().toISOString() })
        .eq('property_id', propertyId)
        .eq('status', 'active');

      if (skipped > 0) {
        alert(`Imported ${newRooms.length} rooms. Skipped ${skipped} duplicate room number${skipped > 1 ? 's' : ''}.`);
      }

      await onImport();
      onClose();
    } catch (err) {
      alert('Error importing rooms: ' + (err as any).message);
    } finally {
      setImporting(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal modal-lg" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2 className="modal-title">Import Rooms from CSV</h2>
          <button className="modal-close" onClick={onClose}>&times;</button>
        </div>
        <div className="modal-body">
          <p className="mb-md text-muted">
            Import multiple rooms from a CSV file. Download the template below for the correct format.
          </p>

          <button className="btn btn-secondary mb-md" onClick={downloadTemplate}>
            📄 Download Template
          </button>

          {/* File Drop Zone */}
          <div
            className={`file-drop-zone ${dragOver ? 'drag-over' : ''}`}
            onClick={() => (document.getElementById('csv-file-input') as HTMLInputElement | null)?.click()}
            onDragOver={(e: React.DragEvent<HTMLDivElement>) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={handleDrop}
          >
            <div className="file-drop-zone-icon">📥</div>
            <p className="text-muted">
              {fileName ? `Selected: ${fileName}` : 'Drop CSV file here or click to browse'}
            </p>
            <input
              id="csv-file-input"
              type="file"
              accept=".csv"
              style={{ display: 'none' }}
              onChange={handleFileSelect}
            />
          </div>

          {/* Preview Table */}
          {importData.length > 0 && (
            <div className="mt-lg">
              <h4 className="mb-xs">Preview ({importData.length} rooms)</h4>
              <div className="preview-table-container">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Room</th>
                      <th className="text-center">Floor</th>
                      <th>Type</th>
                      <th className="text-center">Beds</th>
                    </tr>
                  </thead>
                  <tbody>
                    {importData.slice(0, 10).map((room, idx) => (
                      <tr key={idx}>
                        <td>{room.room_number}</td>
                        <td className="text-center">{room.floor || '-'}</td>
                        <td>{room.bed_type || room.room_type || 'double'}</td>
                        <td className="text-center">{room.bed_count || 1}</td>
                      </tr>
                    ))}
                    {importData.length > 10 && (
                      <tr>
                        <td colSpan={4} className="text-center text-muted">
                          ... and {importData.length - 10} more
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose} disabled={importing}>
            Cancel
          </button>
          <button
            className="btn btn-primary"
            onClick={handleImport}
            disabled={importing || importData.length === 0}
          >
            {importing ? 'Importing...' : `Import ${importData.length} Room${importData.length !== 1 ? 's' : ''}`}
          </button>
        </div>
      </div>
    </div>
  );
}

export default POSRooms;
